import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import NavigationBar from '@/components/NavigationBar';
import LoadingSpinner from '@/components/LoadingSpinner';
import { AuthContext } from '@/AuthContext';
import { notificationStyles } from '@/utils';
import TradeExplanation from '@/components/TradeExplanation';

const PAGE_SIZE = 25;

const activityParams = ({ view, botId, symbol, eventType, cursor = null }) => ({
  limit: PAGE_SIZE,
  view,
  bot_id: botId || undefined,
  symbol: symbol || undefined,
  event_type: eventType,
  ...(cursor ? { cursor } : {}),
});

const mergeActivity = (current, incoming) => {
  const byId = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => byId.set(item.id, item));
  return [...byId.values()].sort((left, right) => {
    const timeOrder = new Date(right.occurred_at).getTime() - new Date(left.occurred_at).getTime();
    return timeOrder || right.id.localeCompare(left.id);
  });
};

const emptyMessage = (summary) => {
  if (!summary?.bot_count) return 'No Practice activity yet. Create and start a bot to begin.';
  if (!summary?.running_bot_count) return 'Your bot is configured but Practice checks have not started.';
  if (!summary?.successful_market_check_count) return 'Your bot is running. The first successful Market Check is still pending.';
  return 'No additional Practice activity is available yet.';
};

const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const severityStyle = {
  INFO: 'border-blue-200 bg-blue-50/70 dark:border-blue-900 dark:bg-blue-950/20',
  SUCCESS: 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/20',
  ATTENTION: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/25',
};

const LiveLog = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activityView, setActivityView] = useState('meaningful');
  const [selectedBotId, setSelectedBotId] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [symbolDraft, setSymbolDraft] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const isMountedRef = useRef(true);
  const sessionGenerationRef = useRef(0);
  const paginationRef = useRef({ nextCursor: null, hasMore: false });
  const paginationInitializedRef = useRef(false);
  const { token, verifyAndRefreshTokenImmediate, axiosInstance } = useContext(AuthContext);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    sessionGenerationRef.current += 1;
    paginationRef.current = { nextCursor: null, hasMore: false };
    paginationInitializedRef.current = false;
    setItems([]);
    setSummary(null);
    setNextCursor(null);
    setHasMore(false);
    setLastUpdated(null);
    setNotification(null);
    setConnectionStatus('idle');
    setIsLoading(Boolean(token));
  }, [activityView, selectedBotId, selectedEventType, selectedSymbol, token]);

  const fetchRecentActivity = useCallback(async (force = false) => {
    if (!token || (isPaused && !force)) return;
    const requestGeneration = sessionGenerationRef.current;
    try {
      const isValid = await verifyAndRefreshTokenImmediate();
      if (!isValid || !isMountedRef.current || requestGeneration !== sessionGenerationRef.current) return;
      const response = await axiosInstance.get('/api/activity', {
        params: activityParams({ view: activityView, botId: selectedBotId, symbol: selectedSymbol, eventType: selectedEventType }),
        timeout: 15000,
      });
      if (!isMountedRef.current || requestGeneration !== sessionGenerationRef.current) return;
      setItems((current) => mergeActivity(current, response.data?.items || []));
      setSummary(response.data?.summary || null);
      if (!paginationInitializedRef.current) {
        const pagination = { nextCursor: response.data?.next_cursor || null, hasMore: Boolean(response.data?.has_more) };
        paginationRef.current = pagination;
        paginationInitializedRef.current = true;
        setNextCursor(pagination.nextCursor);
        setHasMore(pagination.hasMore);
      }
      setConnectionStatus('active');
      setLastUpdated(new Date());
      setNotification(null);
    } catch {
      if (!isMountedRef.current || requestGeneration !== sessionGenerationRef.current) return;
      setConnectionStatus('error');
      setNotification({ type: 'error', message: 'Activity could not be refreshed. Pinngro will keep trying.' });
    } finally {
      if (isMountedRef.current && requestGeneration === sessionGenerationRef.current) setIsLoading(false);
    }
  }, [activityView, axiosInstance, isPaused, selectedBotId, selectedEventType, selectedSymbol, token, verifyAndRefreshTokenImmediate]);

  const loadOlderActivity = useCallback(async () => {
    if (!token || !nextCursor || isLoadingOlder) return;
    const requestGeneration = sessionGenerationRef.current;
    setIsLoadingOlder(true);
    try {
      const response = await axiosInstance.get('/api/activity', {
        params: activityParams({ view: activityView, botId: selectedBotId, symbol: selectedSymbol, eventType: selectedEventType, cursor: nextCursor }),
        timeout: 15000,
      });
      if (!isMountedRef.current || requestGeneration !== sessionGenerationRef.current) return;
      setItems((current) => mergeActivity(current, response.data?.items || []));
      const pagination = { nextCursor: response.data?.next_cursor || null, hasMore: Boolean(response.data?.has_more) };
      paginationRef.current = pagination;
      paginationInitializedRef.current = true;
      setNextCursor(pagination.nextCursor);
      setHasMore(pagination.hasMore);
      setNotification(null);
    } catch {
      if (isMountedRef.current && requestGeneration === sessionGenerationRef.current) {
        setNotification({ type: 'error', message: 'Older activity could not be loaded. Please try again.' });
      }
    } finally {
      if (isMountedRef.current && requestGeneration === sessionGenerationRef.current) setIsLoadingOlder(false);
    }
  }, [activityView, axiosInstance, isLoadingOlder, nextCursor, selectedBotId, selectedEventType, selectedSymbol, token]);

  const submitSymbolFilter = (event) => {
    event.preventDefault();
    setSelectedSymbol(symbolDraft.trim().toUpperCase());
  };

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return undefined;
    }
    fetchRecentActivity(true);
    const interval = isPaused ? null : setInterval(fetchRecentActivity, 5000);
    return () => { if (interval) clearInterval(interval); };
  }, [fetchRecentActivity, isPaused, token]);

  const renderStatusIndicator = () => {
    let indicatorClass = 'bg-gray-400';
    let statusMessage = 'Loading live updates…';
    if (isPaused) {
      indicatorClass = 'bg-yellow-500';
      statusMessage = 'Live updates paused';
    } else if (connectionStatus === 'active') {
      indicatorClass = 'bg-green-500';
      statusMessage = 'Live updates active';
    } else if (connectionStatus === 'error') {
      indicatorClass = 'bg-red-500';
      statusMessage = 'Live updates unavailable. Retrying…';
    }
    return (
      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400" role="status">
        <span className={`mr-2 inline-block h-2 w-2 rounded-full ${indicatorClass}`} aria-hidden="true" />
        <span>{statusMessage}</span>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen flex-col">
      <NavigationBar />
      <main className="flex-1 bg-gray-50 p-4 pt-16 dark:bg-gray-900 sm:p-6 sm:pt-16">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Activity</h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-300">A plain-language timeline of what your Practice bots did, what needs you, and what happened next. Open the Bot Report for a plain-language explanation of a bot decision and its supporting evidence.</p>
            </div>
            {lastUpdated && <span className="text-sm text-gray-500 dark:text-gray-400">Last refreshed: {lastUpdated.toLocaleTimeString()}</span>}
          </div>

          <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-gray-800" aria-label="Live timeline controls">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setIsPaused((current) => !current)}
                aria-pressed={isPaused}
                className={`${isPaused ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-700 hover:bg-slate-800'} w-full rounded-lg px-5 py-2.5 font-bold text-white transition sm:w-auto`}
              >
                {isPaused ? 'Resume live updates' : 'Pause live updates'}
              </button>
              {renderStatusIndicator()}
            </div>
            <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">Pausing stops only this timeline&apos;s automatic refresh. Your bots and Practice market checks keep running.</p>
          </section>

          <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-gray-800" aria-label="Activity filters">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black text-slate-900 dark:text-white">Show</p>
                <div className="mt-2 inline-flex rounded-lg bg-slate-100 p-1 dark:bg-slate-900" role="group" aria-label="Activity view">
                  <button type="button" data-testid="activity-meaningful" onClick={() => setActivityView('meaningful')} aria-pressed={activityView === 'meaningful'} className={`rounded-md px-3 py-2 text-sm font-bold ${activityView === 'meaningful' ? 'bg-white text-blue-800 shadow-sm dark:bg-slate-700 dark:text-blue-200' : 'text-slate-600 dark:text-slate-300'}`}>Meaningful activity</button>
                  <button type="button" data-testid="activity-all" onClick={() => setActivityView('all')} aria-pressed={activityView === 'all'} className={`rounded-md px-3 py-2 text-sm font-bold ${activityView === 'all' ? 'bg-white text-blue-800 shadow-sm dark:bg-slate-700 dark:text-blue-200' : 'text-slate-600 dark:text-slate-300'}`}>All activity</button>
                </div>
              </div>
              <form onSubmit={submitSymbolFilter} className="grid gap-2 sm:grid-cols-3 lg:w-[42rem]" aria-label="Refine activity">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Bot
                  <select value={selectedBotId} onChange={(event) => setSelectedBotId(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900">
                    <option value="">All bots</option>
                    {(summary?.bot_options || []).map((bot) => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
                  </select>
                </label>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Event type
                  <select value={selectedEventType} onChange={(event) => setSelectedEventType(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900">
                    <option value="all">All event types</option>
                    <option value="proposals">Proposals &amp; decisions</option>
                    <option value="fills">Practice fills</option>
                    <option value="bot_activity">Bot activity</option>
                    <option value="market_checks">Market checks</option>
                  </select>
                </label>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Symbol
                  <div className="mt-1 flex gap-2"><input value={symbolDraft} onChange={(event) => setSymbolDraft(event.target.value)} placeholder="BAC" maxLength="16" className="min-w-0 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm uppercase dark:border-slate-600 dark:bg-slate-900" /><button type="submit" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-blue-700 dark:border-slate-600 dark:text-blue-300">Apply</button></div>
                </label>
              </form>
            </div>
            <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">Meaningful activity keeps proposals, decisions, authorization and virtual fills in view. Market Checks can correctly produce no signal; select All activity to include those routine checks. Filters apply on the server and preserve Activity history.</p>
          </section>

          {notification?.type && <div className="mb-4 rounded-lg border p-4" role="alert" style={notificationStyles[notification.type] || {}}>{notification.message}</div>}

          <section className="rounded-xl bg-white p-4 shadow-lg dark:bg-gray-800 sm:p-8" aria-labelledby="practice-timeline-heading">
            <div className="mb-5">
              <h2 id="practice-timeline-heading" className="text-lg font-black text-slate-900 dark:text-white">Pinngro Practice timeline</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Every transaction shown here is virtual; no Alpaca Paper or Live broker order is submitted.</p>
            </div>
            {!isLoading && summary?.bot_count > 0 && !summary?.successful_market_check_count && (
              <div className="mb-4 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm font-bold text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100" role="status">
                {emptyMessage(summary)}
              </div>
            )}
            <div aria-live="polite">
              {isLoading ? (
                <div className="flex justify-center py-12"><LoadingSpinner /></div>
              ) : items.length > 0 ? (
                <ol className="space-y-3">
                  {items.map((item) => (
                    <li key={item.id} data-activity-id={item.id} data-event-type={item.event_type} className={`rounded-xl border p-4 ${severityStyle[item.severity] || severityStyle.INFO}`}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-black text-slate-900 dark:text-white">{item.title}</h3>
                            {item.severity === 'ATTENTION' && <span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-black text-amber-950 dark:bg-amber-800 dark:text-amber-50">NEEDS ATTENTION</span>}
                          </div>
                          {item.bot?.name && <p className="mt-1 text-xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">{item.bot.name}</p>}
                          <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-300">{item.detail}</p>
                        </div>
                        <time dateTime={item.occurred_at} className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">{formatTime(item.occurred_at)}</time>
                      </div>
                      {item.destination?.path && <Link className="mt-3 inline-flex rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-blue-700 hover:bg-white/70 dark:border-slate-600 dark:text-blue-300 dark:hover:bg-slate-900/40" to={item.destination.path}>{item.destination.label}</Link>}
                      {item.explanation?.subject_type === 'fill' && <TradeExplanation fillId={item.explanation.fill_id} compact />}
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-sm text-gray-600 dark:border-slate-700 dark:text-gray-300">
                  <p className="font-bold text-slate-900 dark:text-white">{emptyMessage(summary)}</p>
                  <p className="mt-2">Nothing here means no Practice action was approved or executed.</p>
                </div>
              )}
            </div>
            {hasMore && nextCursor && (
              <div className="mt-6 flex justify-center">
                <button type="button" disabled={isLoadingOlder} onClick={loadOlderActivity} className="rounded-lg border border-slate-300 px-5 py-2.5 font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-900">
                  {isLoadingOlder ? 'Loading…' : 'Load older activity'}
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export { emptyMessage, mergeActivity };
export default LiveLog;
